import * as React from 'react';
import { useEffect, useRef, useState, useMemo } from 'react';
import { Flashbar, FlashbarProps, SpaceBetween, Tabs } from '@amzn/awsui-components-react-v3';
import { GroupMetadata } from 'aws-sdk/clients/omnismusservice';
import { listSsoGroups, listSsoGroupResources } from 'src/api/omnismus';
import { DEFAULT_CONTENT_TYPE } from 'src/commons/constants';
import { CommonPageProps } from 'src/components/common/CommonPageProps';
import { RegisterSsoGroupForm } from 'src/components/smus/registerSsoGroupForm';
import { AssociateAccountForm } from 'src/components/smus/associateAccountForm';
import { GroupsTable } from 'src/components/smus/groupsTable';
import { AccountsTable } from 'src/components/smus/accountsTable';
import { ProjectsTable } from 'src/components/smus/projectsTable';
import { PageHeader } from 'src/components/common/PageHeader';
import { isOmniSmusAvailable, isProd } from 'src/api/config';
import {
  GroupEntry,
  AccountEntry,
  ProjectEntry,
  extractGroupResources,
  groupMetadataToGroupEntry,
  OmniSmusUnavailableHeader,
} from '../utils/smusUtil';

const GROUPS_PAGE_SIZE = 50;
const RESOURCES_PAGE_SIZE = 50;
const RESOURCE_FETCH_BATCH_SIZE = 10;

export interface SmusResourcesProps extends CommonPageProps {}

export const SmusResources = (props: SmusResourcesProps) => {
  const [selectedGroupEntry, setSelectedGroupEntry] = useState<GroupEntry | undefined>(undefined);
  const [selectedAccountEntry, setSelectedAccountEntry] = useState<AccountEntry | undefined>(undefined);
  const [groups, setGroups] = useState<GroupEntry[]>([]);
  const [accounts, setAccounts] = useState<AccountEntry[]>([]);
  const [projects, setProjects] = useState<ProjectEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [groupFormModalVisible, setGroupFormModalVisible] = useState(false);
  const [accountFormModalVisible, setAccountFormModalVisible] = useState(false);
  const [notifications, setNotifications] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [hasMoreState, setHasMoreState] = useState(false);

  const groupsNextTokenRef = useRef<string | undefined>(undefined);
  const resourcePaginationRef = useRef<{ group: GroupEntry; nextToken: string | null }[]>([]);

  useEffect(() => {
    props.setContentType(DEFAULT_CONTENT_TYPE);
    handleRefresh();
  }, []);

  const groupOptions = useMemo(
    () => groups.map((group) => ({ teamId: group.teamId, groupName: group.name })),
    [groups],
  );

  const updateHasMore = () => setHasMoreState(!!groupsNextTokenRef.current || resourcePaginationRef.current.length > 0);

  const paginateSsoGroups = async () => {
    const groupList: GroupEntry[] = [];
    const newPaginationEntries: { group: GroupEntry; nextToken: string | null }[] = [];
    const response = await listSsoGroups({
      userName: props.username,
      nextToken: groupsNextTokenRef.current,
      maxResults: GROUPS_PAGE_SIZE,
    });
    groupList.push(
      ...(response.groupMetadataList ?? [])
        .map((metadata) => metadata.groupMetadata)
        .filter((group): group is GroupMetadata => !!group)
        .map((group) => groupMetadataToGroupEntry(group)),
    );
    newPaginationEntries.push(
      ...(response.groupMetadataList ?? [])
        .map((metadata) => metadata.groupMetadata)
        .filter((group): group is GroupMetadata => !!group)
        .map((group) => ({ group: groupMetadataToGroupEntry(group), nextToken: null })),
    );
    groupsNextTokenRef.current = response.nextToken;
    setGroups((prev) => [...prev, ...groupList]);
    resourcePaginationRef.current.push(...newPaginationEntries);
  };

  const batchPaginateSsoGroupResources = async () => {
    const batch = resourcePaginationRef.current.splice(0, RESOURCE_FETCH_BATCH_SIZE);
    const results = await Promise.allSettled(
      batch.map(async (entry) => {
        const res = await listSsoGroupResources({
          groupMetadataId: entry.group.metadataId,
          nextToken: entry.nextToken,
          maxResults: RESOURCES_PAGE_SIZE,
        });
        const { newAccounts, newProjects } = extractGroupResources(entry.group, res.resourceMetadataList ?? []);
        return { entry, newAccounts, newProjects, nextToken: res.nextToken };
      }),
    );

    const allAccounts: AccountEntry[] = [];
    const allProjects: ProjectEntry[] = [];
    const errorNotifications: FlashbarProps.MessageDefinition[] = [];

    for (const result of results) {
      if (result.status === 'fulfilled') {
        allAccounts.push(...result.value.newAccounts);
        allProjects.push(...result.value.newProjects);
        if (result.value.nextToken) {
          resourcePaginationRef.current.push({ group: result.value.entry.group, nextToken: result.value.nextToken });
        }
      } else {
        const failedEntry = batch[results.indexOf(result)];
        errorNotifications.push({
          type: 'error',
          content: `Failed to load resources for group ${failedEntry.group.name} with next token ${failedEntry.nextToken}`,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        });
        resourcePaginationRef.current.push(failedEntry);
      }
    }

    setNotifications((prev) => [...prev, ...errorNotifications]);
    setAccounts((prev) => [...prev, ...allAccounts]);
    setProjects((prev) => [...prev, ...allProjects]);
  };

  const handleRefresh = async () => {
    if (!isOmniSmusAvailable()) return;
    setLoading(true);
    setLoadFailed(false);
    setGroups([]);
    setAccounts([]);
    setProjects([]);
    groupsNextTokenRef.current = undefined;
    resourcePaginationRef.current = [];
    try {
      await paginateSsoGroups();
      await batchPaginateSsoGroupResources();
    } catch (err) {
      console.error('Failed to list SSO groups', err);
      const message = err instanceof Error ? err.message : String(err);
      const sandboxMessage = isProd()
        ? ''
        : 'Sandbox domain may require additional onboarding. If the issue persists, please contact us. ';

      setLoadFailed(true);
      setNotifications([
        {
          type: 'error',
          content: `${sandboxMessage}Failed to load resources: ${message}`,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
    setLoading(false);
    updateHasMore();
  };

  const handleLoadMore = async () => {
    setLoading(true);
    try {
      if (groupsNextTokenRef.current) {
        await paginateSsoGroups();
      }
      if (resourcePaginationRef.current.length > 0) {
        await batchPaginateSsoGroupResources();
      }
    } catch (err) {
      console.error('Failed to load more', err);
      const message = err instanceof Error ? err.message : String(err);
      setNotifications([
        {
          type: 'error',
          content: `Failed to load more resources: ${message}`,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
    setLoading(false);
    updateHasMore();
  };

  const buttonOptions = () => {
    const buttons = [];
    if (hasMoreState) {
      buttons.push({
        text: 'Load more resources...',
        variant: 'normal',
        onItemClick: handleLoadMore,
        disabled: !hasMoreState || loading,
      });
    }
    buttons.push({
      text: '',
      icon: 'refresh',
      onItemClick: handleRefresh,
    });
    return buttons;
  };

  if (!isOmniSmusAvailable()) {
    return <OmniSmusUnavailableHeader />;
  }

  return (
    <SpaceBetween size='m'>
      <Flashbar items={notifications} />
      <PageHeader
        buttons={buttonOptions()}
        header={`AWS-SMUS${isProd() ? '' : ' sandbox'} domain resources`}
        variant='h1'
      />
      <Tabs
        tabs={[
          {
            label: `SSO groups (${groups.length})`,
            id: 'groups',
            content: (
              <GroupsTable
                {...props}
                groups={groups}
                loading={loading}
                loadFailed={loadFailed}
                title='SSO groups you are a member of'
                onRegister={() => {
                  setSelectedGroupEntry(undefined);
                  setGroupFormModalVisible(true);
                }}
                onEdit={(group) => {
                  setSelectedGroupEntry(group);
                  setGroupFormModalVisible(true);
                }}
              />
            ),
          },
          {
            label: `Associated accounts (${accounts.length})`,
            id: 'accounts',
            content: (
              <AccountsTable
                {...props}
                accounts={accounts}
                loading={loading}
                loadFailed={loadFailed}
                title='Associated accounts onboarded by your SSO groups'
                onAssociate={() => {
                  setSelectedAccountEntry(undefined);
                  setAccountFormModalVisible(true);
                }}
                onEdit={(account) => {
                  setSelectedAccountEntry(account);
                  setAccountFormModalVisible(true);
                }}
              />
            ),
          },
          {
            label: `SMUS projects (${projects.length})`,
            id: 'projects',
            content: (
              <ProjectsTable
                {...props}
                projects={projects}
                loading={loading}
                title='SMUS projects hosted in your associated accounts'
              />
            ),
          },
        ]}
      />
      <RegisterSsoGroupForm
        visible={groupFormModalVisible}
        editGroup={selectedGroupEntry}
        onDismiss={() => setGroupFormModalVisible(false)}
        onSuccess={() => {
          setGroupFormModalVisible(false);
          setNotifications([
            {
              type: 'success',
              content: `SSO group ${selectedGroupEntry ? 'updated' : 'registered'} successfully. ${
                selectedGroupEntry ? '' : 'It can take up to an hour to appear due to membership sync.'
              }`,
              dismissible: true,
              onDismiss: () => setNotifications([]),
            },
          ]);
          handleRefresh();
        }}
        onError={(msg) => {
          setGroupFormModalVisible(false);
          setNotifications([
            {
              type: 'error',
              content: `Failed to ${selectedGroupEntry ? 'update' : 'register'} SSO group: ${msg}`,
              dismissible: true,
              onDismiss: () => setNotifications([]),
            },
          ]);
        }}
      />
      <AssociateAccountForm
        visible={accountFormModalVisible}
        onDismiss={() => setAccountFormModalVisible(false)}
        groups={groupOptions}
        editAccount={selectedAccountEntry}
        onSuccess={() => {
          setAccountFormModalVisible(false);
          setNotifications([
            {
              type: 'success',
              content: `Account ${selectedAccountEntry ? 'updated' : 'association invite sent'} successfully.`,
              dismissible: true,
              onDismiss: () => setNotifications([]),
            },
          ]);
          handleRefresh();
        }}
        onError={(msg) => {
          setAccountFormModalVisible(false);
          setNotifications([
            {
              type: 'error',
              content: `Failed to ${selectedAccountEntry ? 'update' : 'associate'} account: ${msg}`,
              dismissible: true,
              onDismiss: () => setNotifications([]),
            },
          ]);
        }}
      />
    </SpaceBetween>
  );
};
